import React, { useState, useMemo } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";

// Fonction de validation des numéros
const isValidPhoneNumber = (numero) => {
  // Validation pour les numéros Gabon: commence par 241 suivi de 8 chiffres
  const gabonRegex = /^241[0-9]{8}$/;
  
  // Validation basique pour autres formats internationaux
  const internationalRegex = /^\+?[0-9]{10,15}$/;
  
  return gabonRegex.test(numero) || internationalRegex.test(numero);
};

// Fonction de formatage des numéros
const formatPhoneNumber = (numero) => {
  if (!numero) return numero;
  
  // Si c'est un numéro Gabon (241 + 8 chiffres)
  if (numero.startsWith('241') && numero.length === 11) {
    return `+241 ${numero.substring(3, 5)} ${numero.substring(5, 8)} ${numero.substring(8)}`;
  }
  
  return numero;
};

// Exemple de données pour les contacts avec numéros invalides
const contacts = [
  { 
    id: 1, 
    nom: "DUPONT", 
    prenom: "Marie", 
    numero: "24177841332", 
    groupes: "Clients VIP", 
    dateCreation: "15/10/2025 10:29", 
    fonction: "Commercial",
    options: true
  },
  { 
    id: 2, 
    nom: "MARTIN", 
    prenom: "Pierre", 
    numero: "24177513526", 
    groupes: "Fournisseurs", 
    dateCreation: "15/10/2025 10:24", 
    fonction: "Manager",
    options: true
  },
  { 
    id: 3, 
    nom: "BERNARD", 
    prenom: "Sophie", 
    numero: "24176239722", 
    groupes: "Employés", 
    dateCreation: "15/10/2025 10:22", 
    fonction: "RH",
    options: true
  },
  { 
    id: 4, 
    nom: "LEBRUN", 
    prenom: "Luc", 
    numero: "24177445566", 
    groupes: "Clients", 
    dateCreation: "15/10/2025 09:15", 
    fonction: "Technicien",
    options: true
  },
  { 
    id: 5, 
    nom: "ROUX", 
    prenom: "Isabelle", 
    numero: "24177998877", 
    groupes: "Partenaires", 
    dateCreation: "14/10/2025 16:45", 
    fonction: "Directrice",
    options: true
  },
  { 
    id: 6, 
    nom: "DURAND", 
    prenom: "Paul", 
    numero: "12345", // Numéro invalide
    groupes: "Clients", 
    dateCreation: "14/10/2025 14:30", 
    fonction: "Consultant",
    options: true
  },
  { 
    id: 7, 
    nom: "LEROY", 
    prenom: "Catherine", 
    numero: "241123", // Numéro invalide (trop court)
    groupes: "Fournisseurs", 
    dateCreation: "14/10/2025 11:20", 
    fonction: "Marketing",
    options: true
  },
  { 
    id: 8, 
    nom: "MOREAU", 
    prenom: "Antoine", 
    numero: "24177ABC123", // Numéro invalide (caractères non numériques)
    groupes: "Partenaires", 
    dateCreation: "13/10/2025 16:15", 
    fonction: "Développeur",
    options: true
  },
];

export default function ContactManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortField, setSortField] = useState('dateCreation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(true);

  // Filtrage et tri des données
  const filteredAndSortedItems = useMemo(() => {
    let filtered = contacts.filter(item => {
      const matchesSearch = 
        item.nom.toLowerCase().includes(filterText.toLowerCase()) ||
        item.prenom.toLowerCase().includes(filterText.toLowerCase()) ||
        item.numero.includes(filterText) ||
        item.groupes.toLowerCase().includes(filterText.toLowerCase()) ||
        item.fonction.toLowerCase().includes(filterText.toLowerCase());
      
      const isInvalid = !isValidPhoneNumber(item.numero);
      
      if (showOnlyInvalid) {
        return matchesSearch && isInvalid;
      }
      
      return matchesSearch;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Tri spécial pour la validité des numéros
      if (sortField === 'numero') {
        const aValid = isValidPhoneNumber(a.numero);
        const bValid = isValidPhoneNumber(b.numero);
        if (aValid !== bValid) {
          return sortDirection === 'asc' ? 
            (aValid ? 1 : -1) : (aValid ? -1 : 1);
        }
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [filterText, sortField, sortDirection, showOnlyInvalid]);

  // Statistiques
  const stats = useMemo(() => {
    const total = contacts.length;
    const invalid = contacts.filter(contact => !isValidPhoneNumber(contact.numero)).length;
    const valid = total - invalid;
    return { total, valid, invalid };
  }, []);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContacts(currentItems.map(contact => contact.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedContacts.length === currentItems.length;
  const isSomeSelected = selectedContacts.length > 0 && selectedContacts.length < currentItems.length;

  // Actions de masse pour les numéros invalides
  const handleBulkValidate = () => {
    const invalidSelected = selectedContacts.filter(id => {
      const contact = contacts.find(c => c.id === id);
      return contact && !isValidPhoneNumber(contact.numero);
    });
    
    if (invalidSelected.length === 0) {
      alert("Aucun numéro invalide sélectionné");
      return;
    }
    
    // Ici vous pouvez implémenter la logique de correction des numéros
    alert(`${invalidSelected.length} numéro(s) invalide(s) sélectionné(s) pour correction`);
  };

  // --- Fonctions d'export ---
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedContacts.length > 0
      ? contacts.filter(contact => selectedContacts.includes(contact.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) { 
      alert("Aucune donnée à exporter"); 
      setExportLoading(false); 
      return; 
    }

    // Ajouter la colonne de statut de validation pour l'export
    const enhancedData = dataToExport.map(contact => ({
      ...contact,
      statutNumero: isValidPhoneNumber(contact.numero) ? "Valide" : "Invalide",
      numeroFormate: isValidPhoneNumber(contact.numero) ? formatPhoneNumber(contact.numero) : contact.numero
    }));

    if (format === "csv") {
      const headers = ["Nom","Prénom","Numéro","Numéro Formaté","Groupes","Date Création","Fonction","Statut Numéro"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...enhancedData.map(c => [
          c.nom,
          c.prenom,
          c.numero,
          c.numeroFormate,
          c.groupes,
          c.dateCreation,
          c.fonction,
          c.statutNumero
        ].map(v=>`"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-contacts-${Date.now()}.csv`);

    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(enhancedData.map(c => ({
        Nom: c.nom,
        Prénom: c.prenom,
        Numéro: c.numero,
        "Numéro Formaté": c.numeroFormate,
        Groupes: c.groupes,
        "Date Création": c.dateCreation,
        Fonction: c.fonction,
        "Statut Numéro": c.statutNumero
      })));
      
      // Définir les largeurs de colonnes
      const columnWidths = [
        { wch: 15 }, // Nom
        { wch: 15 }, // Prénom
        { wch: 15 }, // Numéro
        { wch: 15 }, // Numéro Formaté
        { wch: 20 }, // Groupes
        { wch: 18 }, // Date Création
        { wch: 15 }, // Fonction
        { wch: 12 }  // Statut Numéro
      ];
      ws['!cols'] = columnWidths;
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts");
      XLSX.writeFile(wb, `export-contacts-${Date.now()}.xlsx`);

    } else if (format === "json") {
      downloadFile(JSON.stringify(enhancedData, null, 2), "application/json;charset=utf-8;", `export-contacts-${Date.now()}.json`);

    } else if (format === "pdf") {
      generatePDF(enhancedData);
    }

    setExportLoading(false);
  };

  // Fonction pour générer PDF
  const generatePDF = (data) => {
    const doc = new jsPDF();
    
    // Configuration des couleurs
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];
    const invalidColor = [231, 76, 60]; // Rouge pour les invalides
    
    // === EN-TÊTE PRINCIPAL ===
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');
    
    // Titre principal
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("GESTION DES CONTACTS", 105, 15, { align: 'center' });
    
    // Informations de l'export
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 15, 22);
    doc.text(`Total: ${data.length} contact(s)`, 180, 22, { align: 'right' });
    
    // === CONFIGURATION DU TABLEAU ===
    const pageWidth = 210;
    const margin = 10;
    const tableTop = 35;
    let yPosition = tableTop;
    
    // Largeurs des colonnes
    const colWidths = [22, 22, 22, 25, 35, 25, 15];
    const headers = ["NOM", "PRÉNOM", "NUMÉRO", "GROUPES", "DATE CRÉATION", "FONCTION", "STATUT"];
    
    // === EN-TÊTES DU TABLEAU ===
    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    
    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    
    yPosition += 6;
    
    // === DONNÉES DU TABLEAU ===
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    
    data.forEach((item, index) => {
      // Vérifier si on doit créer une nouvelle page
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        
        // Redessiner les en-têtes du tableau sur la nouvelle page
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
      }
      
      // Fond alterné pour les lignes
      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }
      
      // Préparer les données avec formatage
      const isInvalid = item.statutNumero === "Invalide";
      const rowData = [
        item.nom,
        item.prenom,
        item.numero,
        item.groupes.length > 15 ? item.groupes.substring(0, 15) + '...' : item.groupes,
        item.dateCreation,
        item.fonction,
        item.statutNumero
      ];
      
      // Dessiner les données de la ligne
      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        // Couleur rouge pour les numéros invalides dans la colonne statut
        if (cellIndex === 6 && isInvalid) {
          doc.setTextColor(...invalidColor);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });
      
      yPosition += 6;
    });
    
    // === PIED DE PAGE ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Ligne de séparation
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      
      // Numéro de page
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
      
      // Signature
      doc.text("Système de Gestion des Contacts", margin, 282);
      doc.text(new Date().getFullYear().toString(), pageWidth - margin, 282, { align: 'right' });
    }
    
    // === SAUVEGARDE ===
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`contacts-${timestamp}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <i 
          className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill position-absolute ms-1`}
          style={{ fontSize: '12px' }}
        ></i>
      )}
    </th>
  );

  return (
    <div className="main main-app p-3 p-lg-4">
      <div className="container-fluid">
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1 fw-bold">Gestion des numéros invalides</h4>
                <p className="text-muted mb-0">
                  Recherche et gestion de votre carnet d'adresses
                </p>
              </div>
              <div className="d-flex gap-2">
                {selectedContacts.length > 0 && (
                  <>
                    <Button variant="outline-secondary" size="sm">
                      <i className="bi bi-people me-2"></i>
                      Ajouter au groupe ({selectedContacts.length})
                    </Button>
                    <Button 
                      variant="outline-warning" 
                      size="sm"
                      onClick={handleBulkValidate}
                      title="Corriger les numéros invalides sélectionnés"
                    >
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Corriger numéros ({selectedContacts.filter(id => {
                        const contact = contacts.find(c => c.id === id);
                        return contact && !isValidPhoneNumber(contact.numero);
                      }).length})
                    </Button>
                    <Button variant="outline-danger" size="sm">
                      <i className="bi bi-trash me-2"></i>
                      Supprimer ({selectedContacts.length})
                    </Button>
                  </>
                )}
                <Dropdown>
                  <Dropdown.Toggle 
                    variant="outline-primary" 
                    disabled={exportLoading}
                  >
                    <i className={`bi ${exportLoading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                    {exportLoading ? 'Export...' : 'Exporter'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item 
                      onClick={() => handleExport('csv')}
                      disabled={exportLoading}
                    >
                      <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                      Export CSV
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => handleExport('excel')}
                      disabled={exportLoading}
                    >
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Export Excel
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => handleExport('pdf')}
                      disabled={exportLoading}
                    >
                      <i className="bi bi-file-earmark-pdf me-2"></i>
                      Export PDF
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => handleExport('json')}
                      disabled={exportLoading}
                    >
                      <i className="bi bi-file-earmark-code me-2"></i>
                      Export JSON
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Button variant="primary">
                  <i className="bi bi-person-plus me-2"></i>
                  Nouveau contact
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Statistiques */}
        <Row className="mb-4">
  <Col md={4} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-primary text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-people me-2 fs-6"></i>
          <span className="small fw-semibold">TOTAL NUMÉROS</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.total}</h3>
          <small className="text-muted">Numéros importés</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  <Col md={4} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-success text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-check-circle me-2 fs-6"></i>
          <span className="small fw-semibold">NUMÉROS VALIDES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.valid}</h3>
          <small className="text-muted">En état d'utilisation</small>
        </div>
      </Card.Body>
    </Card>
  </Col>

  <Col md={4} className="mb-3">
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-warning text-white py-2">
        <div className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle me-2 fs-6"></i>
          <span className="small fw-semibold">NUMÉROS INVALIDES</span>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="text-center">
          <h3 className="fw-bold text-dark mb-0">{stats.invalid}</h3>
          <small className="text-muted">À vérifier</small>
        </div>
      </Card.Body>
    </Card>
  </Col>
</Row>

        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-people me-2 fs-5"></i>
                  <span className="fw-semibold">Liste des numéros invalides</span>
                </div>
                <div className="text-white-50 small d-flex align-items-center">
                  <i className="bi bi-info-circle me-1"></i>
                  <span>{filteredAndSortedItems.length} contact(s)</span>
                  {selectedContacts.length > 0 && (
                    <span className="ms-2">
                      • {selectedContacts.length} sélectionné(s)
                    </span>
                  )}
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {/* Barre d'outils */}
                <div className="p-3 border-bottom bg-light">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-white">
                          <i className="bi bi-search text-muted"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, prénom, numéro..."
                          value={filterText}
                          onChange={e => setFilterText(e.target.value)}
                          className="border-start-0"
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => setFilterText('')}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Form.Check
                          type="switch"
                          id="invalid-only"
                          label="Afficher seulement les numéros invalides"
                          checked={showOnlyInvalid}
                          onChange={(e) => setShowOnlyInvalid(e.target.checked)}
                          className="me-3"
                        />
                        <Button variant="outline-secondary" size="sm" className="me-2">
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Actualiser
                        </Button>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-list-ul me-2"></i>
                            {itemsPerPage} par page
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setItemsPerPage(5)}>5 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(10)}>10 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(25)}>25 par page</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Tableau */}
                <div className="table-responsive">
                  <Table hover className="mb-0 table-striped">
                    <thead className="bg-secondary text-white">
                      <tr>
                        <th style={{ width: '40px' }}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                              checked={isAllSelected}
                              ref={input => {
                                if (input) {
                                  input.indeterminate = isSomeSelected;
                                }
                              }}
                              onChange={handleSelectAll}
                            />
                          </div>
                        </th>
                        <SortableHeader field="nom">NOM</SortableHeader>
                        <SortableHeader field="prenom">PRÉNOM</SortableHeader>
                        <SortableHeader field="numero">NUMÉRO</SortableHeader>
                        <SortableHeader field="groupes">GROUPES</SortableHeader>
                        <SortableHeader field="dateCreation">DATE DE CRÉATION</SortableHeader>
                        <SortableHeader field="fonction">FONCTION</SortableHeader>
                        <th>STATUT</th>
                        <th>OPTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((contact) => {
                          const isValid = isValidPhoneNumber(contact.numero);
                          return (
                            <tr key={contact.id} className="align-middle">
                              <td>
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{
                                      borderRadius: '50%',
                                      width: '18px',
                                      height: '18px',
                                      cursor: 'pointer'
                                    }}
                                    checked={selectedContacts.includes(contact.id)}
                                    onChange={() => handleSelectContact(contact.id)}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="fw-bold">{contact.nom}</div>
                              </td>
                              <td>
                                <div className="fw-medium">{contact.prenom}</div>
                              </td>
                              <td>
                                <code className={isValid ? "" : "text-danger"}>
                                  {isValid ? formatPhoneNumber(contact.numero) : contact.numero}
                                </code>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">{contact.groupes}</span>
                              </td>
                              <td>
                                <small className="text-muted">{contact.dateCreation}</small>
                              </td>
                              <td>
                                <span className="text-muted">{contact.fonction}</span>
                              </td>
                              <td>
                                <Badge 
                                  bg={isValid ? "success" : "warning"} 
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ minWidth: "80px" }}
                                >
                                  <i className={`bi ${isValid ? "bi-check-circle" : "bi-exclamation-triangle"} me-1`}></i>
                                  {isValid ? "Valide" : "Invalide"}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    title="Modifier"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm"
                                    title="Voir les détails"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </Button>
                                  {!isValid && (
                                    <Button 
                                      variant="outline-warning" 
                                      size="sm"
                                      title="Corriger le numéro"
                                    >
                                      <i className="bi bi-tools"></i>
                                    </Button>
                                  )}
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    title="Supprimer"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-5">
                            <i className="bi bi-people display-4 text-muted"></i>
                            <p className="mt-3 text-muted">VOUS N'AVEZ AUCUN CONTACT.</p>
                            <p className="text-muted mb-3">VOUS POUVEZ EN CRÉER EN APPUYANT SUR LE BOUTON CI-DESSUS.</p>
                            {filterText && (
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => setFilterText('')}
                                className="me-2"
                              >
                                Réinitialiser la recherche
                              </Button>
                            )}
                            {showOnlyInvalid && (
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={() => setShowOnlyInvalid(false)}
                              >
                                Afficher tous les contacts
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-3 border-top d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} contacts
                      {selectedContacts.length > 0 && (
                        <span className="ms-2 text-primary">
                          • {selectedContacts.length} sélectionné(s)
                        </span>
                      )}
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {[...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                          key={index + 1}
                          active={index + 1 === currentPage}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* CSS pour l'animation de chargement */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Footer />
    </div>
  );
}